<template>
    <div class="date-range-v2 d-md-inline-block">
        <div class="btn-group d-flex d-md-inline-flex">
            <!-- Raccourci vers la date ou la période suggérée (ex : aujourd'hui sur le planning) -->
            <button
                v-if="hasSuggestion"
                class="btn btn-outline-primary flex-grow-0"
                v-b-tooltip.hover="suggestedLabel"
                @click.prevent="applySuggestedDate"
            >
                <font-awesome-icon :icon="['fal', 'calendar']" />
            </button>
            <!-- Bouton résumé de la période sélectionnée -->
            <button class="btn btn-outline-primary flex-grow-1" @click.prevent="modal_show = true">
                <font-awesome-icon v-if="!hasSuggestion" :icon="['fal', 'calendar']" /> {{ rangeLabel }}
            </button>
            <!-- Relance de la recherche sans ouvrir la modale (masqué sur mobile hors formulaire) -->
            <button
                v-if="showSubmitButton"
                class="btn btn-outline-primary flex-grow-0"
                v-b-tooltip.hover="$t('global.rechercher')"
                @click.prevent="onSubmit"
            >
                <font-awesome-icon :icon="['fal', 'search']" />
            </button>
        </div>

        <!-- Modale de sélection de la période -->
        <b-modal v-model="modal_show" :title="$t('date_range.selectionner_periode')" hide-footer>
            <div class="row">
                <div class="col-12 col-md-6">
                    <label>{{ startLabel }}</label>
                    <e-datepicker v-model="internalStart" :max-date="internalEnd" @input="SetPerso()"></e-datepicker>
                </div>
                <div class="col-12 col-md-6 mt-2 mt-md-0">
                    <label>{{ endLabel }}</label>
                    <e-datepicker v-model="internalEnd" :min-date="internalStart" @input="SetPerso()"></e-datepicker>
                </div>
            </div>

            <!-- Boutons rapides des périodes prédéfinies -->
            <div v-if="dateSelect" class="d-flex flex-wrap mt-3">
                <button
                    v-for="period in quickPeriods"
                    :key="period"
                    class="btn btn-sm mr-2 mb-2"
                    :class="selected_period === period ? 'btn-primary' : 'btn-secondary'"
                    @click.prevent="selectPeriod(period)"
                >
                    {{ $t('date_range.'+period) }}
                </button>
            </div>

            <button v-if="submitButton" @click.prevent="submitAndClose" class="btn btn-block btn-primary mt-2">{{ $t('global.rechercher') }}</button>
        </b-modal>
    </div>
</template>

<script>
import Window from '@/mixins/Window.js'
import Tools from '@/mixins/Tools.js'

export default {
    name: 'DateRangev2',
    mixins: [Window, Tools],
    props: {
        start: {
            type: Date,
            default: () => (null)
        },
        startDefault: {
            type: Date,
            default: () => (null)
        },
        startLabel: {
            type: String,
            default: function() { return this.$t('global.date_debut') }
        },
        end: {
            type: Date,
            default: () => (null)
        },
        endDefault: {
            type: Date,
            default: () => (null)
        },
        endLabel: {
            type: String,
            default: function() { return this.$t('global.date_fin') }
        },
        inModal: {
            type: Boolean,
            default: function() { return false }
		},
        submitAtCreation: {
            type: Boolean,
            default: function() { return true }
		},
		persistId: {
			type: String,
			default: ''
		},
        dateSelect: {
            type: Boolean,
            default: function() { return false }
		},
        periodLabel: {
            type: String,
            default: function() { return 'personalise'}
		},
        periodToShow: {
            type: Array,
            default: function() { return null }
        },
        submitButton: {
            type: Boolean,
            default: function() { return true }
        },
        submitButtonMobile: {
            type: Boolean,
            default: function() { return false } /* À activer quand le sélecteur accompagne d'autres champs de formulaire, le bouton restant alors visible sur mobile */
        },
        suggestedDate: {
            type: Date,
            default: null /* Si renseignée, affiche un raccourci appliquant cette date (ex : aujourd'hui sur le planning) */
        },
        suggestedRange: {
            type: Object,
            default: null /* { start: Date, end: Date } - raccourci appliquant une période complète (prioritaire sur suggestedDate) */
        },
        suggestedLabel: {
            type: String,
            default: function() { return this.$t('table.general.reset_to_today') } /* Tooltip du bouton raccourci */
        }
    },
    data(){
        return {
            all_periods:[
                'jour',
                'demain',
                'J+2',
                'J+3',
                'hier',
                'semaine',
                'semaine_derniere',
                'semaine_prochaine',
                'mois',
                'mois_dernier',
                'mois_prochain',
                'annee_en_cours',
                'annee_derniere',
                'personalise',
                'six_derniers_mois',
                'trois_derniers_mois'
            ],
            selected_period: 'personalise',
            modal_show: false,
        }
    },
    computed: {
        all_periods_formatted(){
            if(this.periodToShow == null){
                return this.all_periods
            }
            const periods_formatted = this.all_periods.filter(valeur => this.periodToShow.includes(valeur))
            return periods_formatted
        },

        /* Périodes proposées en boutons rapides ("personalise" correspond à la saisie manuelle des dates) */
        quickPeriods() {
            return this.all_periods_formatted.filter(period => period !== 'personalise')
        },

        hasSuggestion() {
            return this.suggestedDate !== null || this.suggestedRange !== null
        },

        /* Le bouton de recherche hors modale n'a d'intérêt sur mobile que si le sélecteur accompagne d'autres champs de formulaire */
        showSubmitButton() {
            return this.submitButton && (this.submitButtonMobile || !this.isBelowMd)
        },

        /* Libellé du bouton : « date de début - date de fin » */
        rangeLabel() {
            return this.formatDate(this.start) + ' - ' + this.formatDate(this.end)
        },

		persistParams() {
			return this.persistId != ''
		},
        internalStart: {
            get() {
                return this.start
            },
            set(val) {
				if(val) {
					val.setHours(0)
					val.setMinutes(0)
					val.setSeconds(0)
				}
				this.$emit('update:start', val)
            }
        },
        internalEnd: {
            get() {
                return this.end
            },
            set(val) {
				if(val) {
					val.setHours(23)
					val.setMinutes(59)
					val.setSeconds(59)
				}
				this.$emit('update:end', val)
            }
        }
	},
	created() {
		if(this.persistParams) {
            const period = this.getDateFilters('period')
            if(period){
                this.selected_period = period
                
                if (this.submitAtCreation) {
                    this.$emit('submit')
                }
                return
            }
			const start = this.getDateFilters('begin')
			if(start) {
				this.$emit('update:start', start)
			}
			else if(this.startDefault) {
				this.$emit('update:start', this.startDefault)
			}

			const end = this.getDateFilters('end')
			if(end) {
				this.$emit('update:end', end)
			}
			else if(this.endDefault) {
				this.$emit('update:end', this.endDefault)
			}

            if (this.submitAtCreation) {
                this.$emit('submit')
            }

		} else {
            this.selected_period = this.periodLabel
            this.checkSelect()

            if (this.submitAtCreation) {
                this.onSubmit()
            }
        }
	},
    methods: {
        SetPerso(){
            this.selected_period = 'personalise'
        },
        formatDateLabel(date) {
            if(!date) return '∞'
            return date.toLocaleDateString(this.$i18n.locale())
        },
        /* Application d'une période prédéfinie depuis les boutons rapides */
        // Un bouton de période rapide vaut demande de recherche, que la recherche à la création soit active ou non
        selectPeriod(period) {
            this.checkSelect(period)
            this.onSubmit()
            this.modal_show = false
        },
        /* Raccourci : applique la période suggérée (ou la journée de la date suggérée) */
        applySuggestedDate() {
            this.selected_period = 'personalise'
            if(this.suggestedRange) {
                this.internalStart = new Date(this.suggestedRange.start)
                this.internalEnd = new Date(this.suggestedRange.end)
            }
            else {
                this.internalStart = new Date(this.suggestedDate)
                this.internalEnd = new Date(this.suggestedDate)
            }
            this.onSubmit()
        },
        submitAndClose() {
            this.onSubmit()
            this.modal_show = false
        },
        onSubmit() {
			if(this.persistParams) {
				this.saveDateFilters('start', this.internalStart)
				this.saveDateFilters('end', this.internalEnd)
			}
            this.$emit('submit')
		},
		getDateFilters(type='begin') {
            const now = new Date()
			const filters = this.$store.getters["table/getTableDate"](this.persistId)
			if(!filters)  {
                if(type == 'begin') return this.start
                else if(type == 'period') return 'personalise'
				return this.end
			}

            if (type == 'period'){
                if( filters.period) return filters.period
                return 'personalise'
            }
            
            if(type == 'begin') return filters.begin
            return filters.end
		},
		saveDateFilters(type, val) {
            const now = new Date()
			this.$store.commit('table/setTableDate', {
				tableId: this.persistId,
				beginDate: type == 'start' ? val : this.internalStart,
				endDate: type == 'end' ? val : this.internalEnd,
                period: this.selected_period,
			})
		},
        checkSelect(value = null){
            if(value){
                this.selected_period = value
            }
            
            let currentDate = new Date()
            const millisec_in_day =  24 * 60 * 60 * 1000
            switch (this.selected_period) {
                case 'jour':
                    this.internalStart = new Date(currentDate.setHours(0,0,0))
                    this.internalEnd = new Date(currentDate.setHours(23,59,59))
                    break
                case 'hier':
                    this.internalStart = new Date(currentDate.getTime() - millisec_in_day)
                    this.internalEnd = new Date(currentDate.getTime() - millisec_in_day)
                    break
                case 'demain':
                    this.internalStart =  new Date(currentDate.getTime() + millisec_in_day)
                    this.internalEnd = new Date(currentDate.getTime() + millisec_in_day)
                    break
                case 'J+2':
                    this.internalStart =  new Date(currentDate.getTime() + millisec_in_day * 2)
                    this.internalEnd = new Date(currentDate.getTime() + millisec_in_day * 2)
                    break
                case 'J+3':
                    this.internalStart =  new Date(currentDate.getTime() + millisec_in_day * 3)
                    this.internalEnd = new Date(currentDate.getTime() + millisec_in_day * 3)
                    break
                case 'semaine_derniere':
                    this.internalStart = new Date(currentDate.getTime() - (7 + currentDate.getDay()) * millisec_in_day )
                    this.internalEnd = new Date(currentDate.getTime() - (1 + currentDate.getDay()) * millisec_in_day )
                    break
                case 'semaine':
                    this.internalStart = new Date(currentDate.getTime() - currentDate.getDay() * millisec_in_day )
                    this.internalEnd = new Date (currentDate.getTime() + (6 - currentDate.getDay()) * millisec_in_day)
                    break
                case 'semaine_prochaine':
                    this.internalStart = new Date(currentDate.getTime() + (7-currentDate.getDay()) * millisec_in_day )
                    this.internalEnd = new Date (currentDate.getTime() + (13-currentDate.getDay()) * millisec_in_day)
                    break
                case 'mois_dernier':
                    this.internalStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
                    this.internalEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
                    break
                case 'six_derniers_mois':
                    this.internalStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 6, 1)
                    this.internalEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
                    break
                case 'trois_derniers_mois':
                    this.internalStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1)
                    this.internalEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
                    break
                case 'mois':
                    this.internalStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
                    this.internalEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
                    break
                case 'mois_prochain':
                    this.internalStart = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
                    this.internalEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0)
                    break
                case 'annee_en_cours':
                    this.internalStart = new Date(currentDate.getFullYear(), 0, 1)
                    this.internalEnd = new Date(currentDate.getFullYear(), 11, 31)
                    break
                case 'annee_derniere':
                    this.internalStart = new Date(currentDate.getFullYear() - 1, 0, 1)
                    this.internalEnd = new Date(currentDate.getFullYear() - 1, 11, 31)
                    break
                default:
                    break
            }
        },
    }
}
</script>
